import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2,
  AlertCircle,
  X,
  Mail,
  Lock,
  Eye,
  EyeOff,
  CheckCircle
} from "lucide-react";
import { loginUser, loginWithSSO } from "../../services/authService";
import { getOrganization } from "../../services/organizationService";
import GoogleSSO from "./GoogleSSO";
import MicrosoftSSO from "./MicrosoftSSO";
import { STORAGE_KEYS } from "../../constants/storage";

export default function LoginPage() {
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [googleUser, setGoogleUser] = useState(null);
  const [microsoftUser, setMicrosoftUser] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();

  // Use ref to prevent duplicate SSO login calls
  const ssoLoginInProgress = useRef(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setShowError(false);

    try {
      const response = await loginUser(formData.email, formData.password);

      if (response.success) {
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.access_token);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.user));

        // Fetch organization data to get organization_id
        try {
          const orgResponse = await getOrganization();

          // Store user details with organization_id
          // Note: API returns 'id' field, not 'organization_id'
          const userDetails = {
            name: response.user.name || formData.email.split("@")[0],
            email: formData.email,
            organization_id: orgResponse.success ? orgResponse.data.id : null,
            role: response.user.role
          };
          localStorage.setItem(
            STORAGE_KEYS.USER_DETAILS,
            JSON.stringify(userDetails)
          );
        } catch (orgError) {
          // If organization fetch fails, store user details without org_id
          const userDetails = {
            name: response.user.name || formData.email.split("@")[0],
            email: formData.email,
            organization_id: null,
            role: response.user.role
          };
          localStorage.setItem(
            STORAGE_KEYS.USER_DETAILS,
            JSON.stringify(userDetails)
          );
        }

        navigate("/dashboard");
      } else {
        setError(response.message || "Login failed. Please try again.");
        setShowError(true);
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const dismissError = () => {
    setShowError(false);
    setError(null);
  };

  const clearMessages = () => {
    setError(null);
    setShowError(false);
    setSuccessMessage(null);
    setShowSuccess(false);
  };

  // Handle SSO Login
  const handleSSOLogin = useCallback(
    async (email, userName = null, provider = "google", credential = null) => {
      // Prevent duplicate calls using ref
      if (ssoLoginInProgress.current) {
        return;
      }

      ssoLoginInProgress.current = true;
      setLoading(true);
      clearMessages();

      try {
        const response = await loginWithSSO(email, provider, credential);

        if (response.success) {
          setSuccessMessage("Authentication successful! Redirecting...");
          setShowSuccess(true);

          localStorage.setItem(
            STORAGE_KEYS.ACCESS_TOKEN,
            response.access_token
          );
          localStorage.setItem(
            STORAGE_KEYS.USER,
            JSON.stringify(response.user)
          );

          // Fetch organization data to get organization_id
          try {
            const orgResponse = await getOrganization();

            // Store user details with organization_id
            // Note: API returns 'id' field, not 'organization_id'
            const userDetails = {
              name: userName || response.user.name || email.split("@")[0],
              email: email,
              organization_id: orgResponse.success ? orgResponse.data.id : null,
              role: response.user.role
            };
            localStorage.setItem(
              STORAGE_KEYS.USER_DETAILS,
              JSON.stringify(userDetails)
            );
          } catch (orgError) {
            // If organization fetch fails, store user details without org_id
            const userDetails = {
              name: userName || response.user.name || email.split("@")[0],
              email: email,
              organization_id: null,
              role: response.user.role
            };
            localStorage.setItem(
              STORAGE_KEYS.USER_DETAILS,
              JSON.stringify(userDetails)
            );
          }

          setTimeout(() => {
            navigate("/dashboard");
          }, 500);
        } else {
          setError(
            response.message || "SSO authentication failed. Please try again."
          );
          setShowError(true);
          ssoLoginInProgress.current = false;
        }
      } catch (err) {
        console.error("SSO Login error:", err);
        setError(
          "An error occurred during SSO authentication. Please try again."
        );
        setShowError(true);
        ssoLoginInProgress.current = false;
      } finally {
        setLoading(false);
      }
    },
    [navigate]
  );

  // Handle Google SSO - only trigger once when googleUser is set
  useEffect(() => {
    if (googleUser && googleUser.email) {
      handleSSOLogin(
        googleUser.email,
        googleUser.name,
        "google",
        googleUser.credential
      );
    }
  }, [googleUser, handleSSOLogin]);

  // Handle Microsoft SSO - only trigger once when microsoftUser is set
  useEffect(() => {
    if (microsoftUser && microsoftUser.email) {
      handleSSOLogin(
        microsoftUser.email,
        microsoftUser.name,
        "microsoft",
        microsoftUser.credential
      );
    }
  }, [microsoftUser, handleSSOLogin]);

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #FCFCFC 0%, #FCFCFC 50%, rgba(0, 111, 234, 0.54) 100%)"
      }}
    >
      {/* Background gradient overlay */}
      <div className="absolute bottom-0 left-0 right-0 h-[50%] bg-gradient-to-t from-blue-100/40 to-transparent" />

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 relative z-10">
        {/* Logo and Title */}
        <div className="flex items-center gap-3 mb-8">
          <img src="/logo.svg" alt="Orbit Logo" className="w-10 h-10" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Nativeorange Orbit
            </h1>
            <p className="text-gray-500 text-sm">
              Integration Core for Agentic Insurance
            </p>
          </div>
        </div>

        {/* Login Card */}
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Welcome back</h2>
            <p className="text-gray-600 text-sm mt-1">
              Sign in to your Orbit account to continue
            </p>
          </div>

          {/* Success Message */}
          {showSuccess && successMessage && (
            <div
              className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 relative"
              role="alert"
            >
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                <div className="ml-3 flex-1">
                  <p className="text-sm text-green-700">{successMessage}</p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {showError && error && (
            <div
              className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 relative"
              role="alert"
            >
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                <div className="ml-3 flex-1">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
                <button
                  type="button"
                  onClick={dismissError}
                  className="ml-auto p-1 text-red-500 hover:bg-red-100 rounded"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@company.com"
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white text-sm"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <button
                  type="button"
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  className="w-full pl-10 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="remember"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="remember" className="ml-2 text-sm text-gray-600">
                Remember me for 30 days
              </label>
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in...
                </span>
              ) : (
                "Sign in"
              )}
            </button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-4 text-gray-500">
                  Or continue with
                </span>
              </div>
            </div>

            {/* Social Login Buttons */}
            <div
              className={`grid grid-cols-2 gap-3 ${loading ? "opacity-50 pointer-events-none" : ""}`}
            >
              <GoogleSSO setGoogleUser={setGoogleUser} disabled={loading} />
              <MicrosoftSSO
                setMicrosoftUser={setMicrosoftUser}
                disabled={loading}
              />
            </div>
          </form>
        </div>
      </div>

      {/* Footer */}
      <div className="py-6 text-center relative z-10">
        <p className="text-sm text-gray-500">
          By signing in, you agree to our{" "}
          <button className="text-blue-600 hover:text-blue-700">
            Terms of Service
          </button>{" "}
          and{" "}
          <button className="text-blue-600 hover:text-blue-700">
            Privacy Policy
          </button>
        </p>
      </div>
    </div>
  );
}
